// Drains the local queue into the database through `sync_batch`.
//
// Every decision here comes from docs/sync-contract.md. The ones that matter:
//
//   - Rows leave the queue only when the server reports them written, in the
//     form they were sent (prune.ts). Never on a timeout or an error: the write
//     may have landed and the response been lost, and resending is safe because
//     every row is idempotent on the server.
//   - One drain per session at a time. Every tap, the app-wide timer and the
//     online event all ask for a sync; overlapping drains re-sent the whole
//     queue over a weak link and raced each other on the same primary keys.

import { supabase } from "./supabase.ts";
import { confirmApplied, loadSession, mutateSession, recordSyncError, type LocalSession } from "./queue.ts";
import { batchIdFor } from "./batchId.ts";
import { pendingRows, type SentBatch } from "./prune.ts";

export interface SyncResult {
  ok: boolean;
  applied: { events: number; taps: number; notes: number };
  deferred: number;
  rejected: { id: string; reason: string }[];
  error?: string;
}

const EMPTY = { events: 0, taps: 0, notes: 0 };
const OK: SyncResult = { ok: true, applied: EMPTY, deferred: 0, rejected: [] };

export async function syncSession(s: LocalSession): Promise<SyncResult> {
  if (pendingRows(s) === 0) return OK;

  const events = s.pendingBlocks.map((b) => ({
    id: b.id,
    category_id: b.categoryId,
    started_at: b.startedAt,
    ended_at: b.endedAt,
    seq: b.seq,
  }));
  const taps = s.pendingTaps.map((t) => ({
    id: t.id, event_id: t.eventId, tapped_at: t.tappedAt,
  }));
  const notes = s.pendingNotes.map((n) => ({
    id: n.id, event_id: n.eventId, category_id: n.categoryId,
    noted_at: n.notedAt, body: n.body,
  }));

  // Exactly what this request carries, captured before it leaves. The response
  // is matched against this, never against the queue as it is by then.
  const sent: SentBatch = {
    blocks: new Map(s.pendingBlocks.map((b) => [b.id, b.endedAt])),
    taps: new Set(s.pendingTaps.map((t) => t.id)),
    notes: new Set(s.pendingNotes.map((n) => n.id)),
  };

  // The batch id is the receipt key and an audit trail of what went up. It is a
  // hash of the payload so two different batches never share one; nothing is
  // pruned on the strength of it any more.
  const batchId = await batchIdFor(s.id, { events, taps, notes });

  const { data, error } = await supabase.rpc("sync_batch", {
    p_session_id: s.id,
    p_batch_id: batchId,
    p_events: events,
    p_taps: taps,
    p_notes: notes,
  });

  if (error) {
    // Nothing is pruned. This is the single most important branch in the file:
    // an error here may still have been a successful write.
    await recordSyncError(s.id, error.message);
    return { ok: false, applied: EMPTY, deferred: 0, rejected: [], error: error.message };
  }

  const res = data as {
    status: string;
    applied: { events: number; taps: number; notes: number };
    deferred: { kind: string; id: string; waiting_on: string }[];
    rejected: { kind: string; id: string; reason: string }[];
  };

  // Any answer other than `applied` prunes nothing. The server no longer sends
  // `duplicate` or `conflict`, and an unknown status must not be read as leave.
  if (res.status !== "applied") {
    const why = `unexpected sync status: ${res.status}`;
    await recordSyncError(s.id, why);
    return { ok: false, applied: EMPTY, deferred: 0, rejected: [], error: why };
  }

  const notConfirmed = new Set([...res.deferred.map((d) => d.id), ...res.rejected.map((r) => r.id)]);
  await confirmApplied(s.id, sent, notConfirmed);

  return {
    ok: true,
    applied: res.applied,
    deferred: res.deferred.length,
    rejected: res.rejected.map((r) => ({ id: r.id, reason: r.reason })),
  };
}

/**
 * Tells the server a session ended. Without this every session on the server
 * looked open forever, indistinguishable from one abandoned by a dead phone.
 */
async function syncSessionEnd(s: LocalSession): Promise<boolean> {
  if (s.endedAt === null || s.endSyncedAt) return true;
  const { data, error } = await supabase
    .from("observation_sessions")
    .update({ ended_at: s.endedAt })
    .eq("id", s.id)
    .is("ended_at", null)
    .select("id");
  if (error) return false;
  // Row level security refuses an update by matching no rows, not by raising,
  // so no error is not the same as written. No row back means an earlier
  // attempt already landed, or this sign-in may not write it; only a read of
  // the row tells which, and only the first counts as sent.
  if (!data || data.length === 0) {
    const { data: row, error: readError } = await supabase
      .from("observation_sessions").select("ended_at").eq("id", s.id).maybeSingle();
    if (readError || !row || row.ended_at === null) return false;
  }
  await mutateSession(s.id, null, (x) => ({ ...x, endSyncedAt: Date.now() }));
  return true;
}

const inflight = new Map<string, Promise<SyncResult>>();
const rerun = new Set<string>();

/**
 * Sends a session's queue until it is empty, the attempts run out, or rows are
 * waiting on something that cannot arrive yet. Bounded on purpose: unbounded
 * retries against a permanent failure are a battery drain in someone's pocket.
 *
 * Single flight per session. A call that arrives mid-drain does not start a
 * second one; it asks for one more pass once the current one finishes, which
 * picks up whatever was queued meanwhile.
 *
 * `ownerId` stops a drain the moment someone else is signed in. Requests carry
 * whoever is signed in when they leave, and a close the database quietly
 * refuses under the wrong sign-in is reported back as applied and pruned.
 */
export function drain(sessionId: string, ownerId?: string, maxAttempts = 5): Promise<SyncResult> {
  const running = inflight.get(sessionId);
  if (running) {
    rerun.add(sessionId);
    return running;
  }
  const run = (async () => {
    let result: SyncResult;
    do {
      rerun.delete(sessionId);
      result = await drainOnce(sessionId, ownerId, maxAttempts);
    } while (result.ok && rerun.has(sessionId));
    return result;
  })().finally(() => inflight.delete(sessionId));
  inflight.set(sessionId, run);
  return run;
}

const signedInAs = async () => (await supabase.auth.getSession()).data.session?.user.id ?? null;

async function drainOnce(sessionId: string, ownerId: string | undefined, maxAttempts: number): Promise<SyncResult> {
  let last: SyncResult = OK;
  const notOwner = async () => ownerId !== undefined && (await signedInAs()) !== ownerId;
  for (let i = 0; i < maxAttempts; i++) {
    if (await notOwner()) return { ...last, ok: false, error: "signed in as someone else" };
    const s = await loadSession(sessionId);
    if (!s) return last;
    if (pendingRows(s) === 0) {
      const ended = await syncSessionEnd(s);
      return ended ? last : { ...last, ok: false, error: "could not record the session end" };
    }

    last = await syncSession(s);
    if (!last.ok) {
      // No wait after the final attempt: it only delayed End session by up to
      // 16 seconds after the outcome was already decided.
      if (i < maxAttempts - 1) await new Promise((r) => setTimeout(r, Math.min(30000, 1000 * 2 ** i)));
      continue;
    }
    // One successful send is a pass. The block still open stays queued by
    // design, so looping here would resend it on every attempt. Deferred rows
    // wait on a parent that is not on the server yet, and resending them at once
    // cannot change that either; the next trigger picks them up.
    const after = await loadSession(sessionId);
    if (after && pendingRows(after) === 0 && !(await notOwner())) {
      const ended = await syncSessionEnd(after);
      if (!ended) return { ...last, ok: false, error: "could not record the session end" };
    }
    return last;
  }
  return last;
}
